import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import { useEffect, useState } from 'react';
import { EventModel, useEventContext } from '../../contexts/EventContext';
import Grid from '@mui/material/Grid';
import { Monad } from '../../monad/Monad';
import { PrivilegeValue, useAuthContext } from '../../contexts/AuthContext';
import { AxiosRequestConfig } from 'axios';
import IconButton from '@mui/material/IconButton';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import SearchIcon from '@mui/icons-material/Search';

import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { User } from 'oidc-client-ts';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { FormControl, InputAdornment, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import { ApiParticipant, MaCheckinResponse, useHttpClientContext } from '../../contexts/HttpClientContext';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import React from 'react';

import { EventInformationModalContent } from './EventInformationTab';
import { ParticipantsAccordion, ParticipantsList } from './ParticipantListTab';
import { QrCodeScanner } from './ScannerTab';
import { useTranslation } from 'react-i18next';
import {
    ApiCheckInResponse,
    CheckInState,
    EventModalNavigationState,
    MaUserLookupResult,
    ScannerCheckInState
} from './EventModalTypes';

export const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

const shouldRenderParticipants = (event: EventModel, user: User) => {
    if (event.source === 'minaaktiviteter') {
        return user.requireMinimumPrivilege(PrivilegeValue.LocalStaff) || user.isDevAdmin();
    }
    const isOfficial = user.requireMinimumPrivilege(PrivilegeValue.LocalActive) || user.isFestivalOfficial() || user.isDevAdmin();
    return (event.visibleParticipantList && (event.booking || !event.preBookingReq)) || ((event.checkInPossible || event.visibleParticipantList) && isOfficial);
}

const canCheckIn = (event: EventModel, user: User) => {
    if (event.source === 'minaaktiviteter') {
        return user.requireMinimumPrivilege(PrivilegeValue.LocalStaff) || user.isDevAdmin();
    }
    if (event.allowedToCheckIn.length > 0) {
        return event.allowedToCheckIn.indexOf(user.getMembershipNumber() ?? user.getFormattedUserId()) > -1;
    }

    return event.checkInPossible && ((user.requireMinimumPrivilege(PrivilegeValue.LocalActive) && user.isFestivalOfficial()) || user.isDevAdmin());
}

const normalizeMembershipNumber = (num: string): string => {
    const upper = (num ?? '').toUpperCase().trim();
    return upper.startsWith('M') ? upper.substring(1) : upper;
};

const membershipNumbersMatch = (a?: string | null, b?: string | null): boolean => {
    if (!a || !b) return false;
    return normalizeMembershipNumber(a) === normalizeMembershipNumber(b);
};

const shouldAlsoCheckInPartner = (checkInResponse: ApiCheckInResponse, participants: ApiParticipant[]) => {

    const partnerBooked = checkInResponse.booking
        && checkInResponse.booking.partnerName
        && checkInResponse.booking.partnerName.length
        && checkInResponse.booking.partnerBooked === 'yes';

    const partner = participants.find(p => membershipNumbersMatch(p.membershipNumber, checkInResponse.booking?.partnerMembershipNumber));

    return partnerBooked && partner && partner.checkedIn === 'no';
}

export const EventModal = () => {

    const eventsContext = useEventContext()
    const authContext = useAuthContext();
    const httpclientContext = useHttpClientContext();
    const { t } = useTranslation();

    const [modalNavigationState, setModalNavigationState] = useState<EventModalNavigationState>(EventModalNavigationState.eventInfo);
    const [participants, setParticipants] = useState<Monad<ApiParticipant[]>>(Monad.idle());
    const [checkInState, setCheckInState] = useState<{ [membershipNumber: string]: CheckInState }>({})
    const [scannerCheckInState, setScannerCheckInState] = useState<ScannerCheckInState>({
        status: 'idle'
    });
    const [scannerSearchOpenRequest, setScannerSearchOpenRequest] = useState(0);

    const [participantSearchTerm, setParticipantSearchTerm] = useState("");
    const [participantCheckInStatusFilter, setParticipantCheckInStatusFilter] = useState<"all" | "checkedIn" | "checkedOut">("all");

    const selectedEvent = eventsContext.selectedEvent;

    const getMaCheckinResult = (payload: MaCheckinResponse | undefined | null) => {
        const responseData = payload as (MaCheckinResponse & Partial<MaCheckinResponse['checkinUser']>) | undefined | null;
        if (responseData?.checkinUser) {
            return responseData.checkinUser;
        }

        if (
            responseData
            && typeof responseData.state === 'number'
            && typeof responseData.code === 'string'
            && typeof responseData.soundSignal === 'string'
            && typeof responseData.fdHeading === 'string'
            && typeof responseData.fdMessage === 'string'
            && typeof responseData.fdBgColor === 'string'
            && typeof responseData.fdTimeOut === 'number'
        ) {
            return {
                state: responseData.state,
                code: responseData.code,
                soundSignal: responseData.soundSignal,
                fdHeading: responseData.fdHeading,
                fdMessage: responseData.fdMessage,
                fdBgColor: responseData.fdBgColor,
                fdTimeOut: responseData.fdTimeOut,
                fdExtraInfo: responseData.fdExtraInfo,
                keyCardRef: responseData.keyCardRef,
            };
        }

        return undefined;
    }

    const fetchParticipants = async (eventId: string) => {
        if (selectedEvent?.source === 'minaaktiviteter') {
            return httpclientContext.authenticatedHttpClient.get<ApiParticipant[]>('/ma-proxy/ma-bookings', { params: { eventId } });
        }
        return httpclientContext.authenticatedHttpClient.get<ApiParticipant[]>('participants?eventId=' + eventId);
    }

    const fetchMaUserByMembershipNumber = async (membershipNumber: string) => {
        const response = await httpclientContext.authenticatedHttpClient.get<MaUserLookupResult[]>('/ma-proxy/ma-user', {
            params: { mshipNumber: membershipNumber }
        });

        if (Array.isArray(response.data) && response.data.length > 0) {
            return response.data[0];
        }

        return null;
    }

    const searchMaUsers = async (textSearch: string) => {
        const response = await httpclientContext.authenticatedHttpClient.get<MaUserLookupResult[]>('/ma-proxy/ma-users', {
            params: { textSearch, maxRows: 1000 }
        });

        return Array.isArray(response.data) ? response.data : [];
    }

    const checkInParticipant = async (eventId: string, membershipNumber: string) => {
        const request = {
            eventId: eventId,
            membershipNumber: membershipNumber
        };
        return httpclientContext.authenticatedHttpClient.post<ApiCheckInResponse>('check-in',
            request);
    }

    const checkOutParticipant = async (eventId: string, membershipNumber: string) => {
        const request = {
            eventId: eventId,
            membershipNumber: membershipNumber
        };
        const options: AxiosRequestConfig<any> = {
            data: request
        }
        return httpclientContext.authenticatedHttpClient.delete<ApiCheckInResponse>('check-in', options);
    }


    const handleCheckIn = async (participant: ApiParticipant) => {
        console.log("Check in", participant);
        const shouldCheckIn = participant.checkedIn !== "yes";

        setCheckInState(prev => ({ ...prev, [participant.membershipNumber]: { status: 'loading' } }));

        if (selectedEvent?.source === 'minaaktiviteter') {
            if (!shouldCheckIn) {
                // One-way check-in only for MA events
                return;
            }
            const request = {
                checkinString: participant.membershipNumber,
                limitToEventId: selectedEvent!.eventId
            };
            httpclientContext.authenticatedHttpClient.post<MaCheckinResponse>('/ma-proxy/checkinUser', request)
                .then(response => {
                    const result = getMaCheckinResult(response.data);
                    if (!result) {
                        throw new Error("Missing MA check-in result payload");
                    }
                    const prev = participants.getOrElse([]);
                    // For successful MA check-in responses, immediately reflect checked-in state in the local list.
                    setParticipants(Monad.success(prev.map(p => p.membershipNumber === participant.membershipNumber ? ({ ...p, checkedIn: "yes" }) : p)));
                    setCheckInState(prevState => ({ ...prevState, [participant.membershipNumber]: { status: 'success', maResult: result } }));
                })
                .catch(error => {
                    console.error("Error checking in MA participant", error.response?.status, error.response?.data ?? error);
                    const responseResult = getMaCheckinResult(error.response?.data);
                    const result = responseResult ?? {
                        state: error.response?.status ?? 500,
                        code: error.code ?? "ERROR",
                        soundSignal: "error",
                        fdHeading: "Fel",
                        fdMessage: error.response?.data?.message ?? "Ett nätverksfel inträffade",
                        fdBgColor: "#cc1922",
                        fdTimeOut: 4000,
                        fdExtraInfo: error.response?.data ? JSON.stringify(error.response.data) : undefined
                    };
                    setCheckInState(prevState => ({ ...prevState, [participant.membershipNumber]: { status: 'error-checkIn', maResult: result } }));
                });
            return;
        }

        (shouldCheckIn
            ? checkInParticipant(selectedEvent!.eventId, participant.membershipNumber)
            : checkOutParticipant(selectedEvent!.eventId, participant.membershipNumber)
        ).then(response => {
            console.log(response.data.checkInStatus);

            var prev = participants.getOrElse([]);
            switch (response.data.checkInStatus) {
                case 'success':
                    setParticipants(Monad.success(prev.map(p => p.membershipNumber === participant.membershipNumber ? ({ ...p, checkedIn: "yes" }) : p)));

                    if (shouldAlsoCheckInPartner(response.data, prev)) {
                        setCheckInState(prevState => ({
                            ...prevState, [participant.membershipNumber]: {
                                status: 'success-ask-partner',
                                partnerParticipant: prev.find(x => membershipNumbersMatch(x.membershipNumber, response.data.booking?.partnerMembershipNumber))
                            }
                        }));
                    } else {
                        setCheckInState(prevState => ({ ...prevState, [participant.membershipNumber]: { status: 'success' } }));
                    }
                    break;
                case 'removed':
                    setParticipants(Monad.success(prev.map(p => p.membershipNumber === participant.membershipNumber ? ({ ...p, checkedIn: "no" }) : p)));
                    setCheckInState(prev => ({ ...prev, [participant.membershipNumber]: { status: 'removed' } }));
                    break;
                case 'alreadyCheckedIn':
                    setParticipants(Monad.success(prev.map(p => p.membershipNumber === participant.membershipNumber ? ({ ...p, checkedIn: "yes" }) : p)));
                    setCheckInState(prev => ({ ...prev, [participant.membershipNumber]: { status: 'alreadyCheckedIn' } }));
                    break;
                default:
                    setCheckInState(prev => ({ ...prev, [participant.membershipNumber]: { status: 'idle' } }));
            }
        }).catch(error => {
            console.error("Error checking in", error);
            setCheckInState(prev => ({ ...prev, [participant.membershipNumber]: { status: shouldCheckIn ? "error-checkIn" : "error-checkOut" } }))
        })

    }

    const handleCheckInPartner = async (partnerParticipant: ApiParticipant, mainParticipant: ApiParticipant) => {

        setCheckInState(prev => ({ ...prev, [partnerParticipant.membershipNumber]: { status: 'loading' } }))

        setCheckInState(prev => ({
            ...prev, [partnerParticipant.membershipNumber]: {
                status: "partner-checkIn-loading"
            }
        }));

        checkInParticipant(selectedEvent!.eventId, partnerParticipant.membershipNumber).then(response => {
            console.log(response.data.checkInStatus);
            var prev = participants.getOrElse([]);

            switch (response.data.checkInStatus) {
                case 'success':
                    setParticipants(Monad.success(prev.map(p => p.membershipNumber === partnerParticipant.membershipNumber ? ({ ...p, checkedIn: "yes" }) : p)));
                    setCheckInState(prev => ({
                        ...prev, [partnerParticipant.membershipNumber]: {
                            status: 'success',
                        }
                    }));

                    setCheckInState(prev => ({
                        ...prev, [mainParticipant.membershipNumber]: {
                            status: 'partner-success',
                            partnerParticipant: partnerParticipant
                        }
                    }));
                    break;
                case 'alreadyCheckedIn':
                    setParticipants(Monad.success(prev.map(p => p.membershipNumber === partnerParticipant.membershipNumber ? ({ ...p, checkedIn: "yes" }) : p)));
                    setCheckInState(prev => ({
                        ...prev, [mainParticipant.membershipNumber]: {
                            status: 'partner-alreadyCheckedIn',
                        }
                    }));
                    break;
                default:
                    setCheckInState(prev => ({ ...prev, [partnerParticipant.membershipNumber]: { status: 'idle' } }));
            }
        }).catch(error => {
            console.error("Error checking in partner", error);
            setCheckInState(prev => ({
                ...prev, [mainParticipant.membershipNumber]: {
                    status: 'partner-error',
                    partnerParticipant: partnerParticipant
                }
            }));
        });
    }

        const handleScannerCheckIn = (scannedText: string, checkOut: boolean): Promise<any> => {

        console.log("handleScannerCheckIn", scannedText);

        // eslint-disable-next-line no-control-regex
        const cleanedText = scannedText.trim().replace(/[\x00-\x1F\x7F-\x9F\u200B-\u200D\uFEFF]/g, '').toUpperCase();

        if (!/^[A-Z0-9]+$/.test(cleanedText)) {
            setScannerCheckInState({ status: "invalidFormat" });
            return Promise.resolve();
        }

        if (selectedEvent!.source === 'minaaktiviteter') {
            setScannerCheckInState({ status: "loading" });

            return httpclientContext.authenticatedHttpClient.post<MaCheckinResponse>('/ma-proxy/checkinUser', {
                checkinString: cleanedText,
                limitToEventId: selectedEvent!.eventId,
            }).then(response => {
                const result = getMaCheckinResult(response.data);
                if (!result) {
                    throw new Error("Missing MA check-in result payload");
                }
                const isSuccess = result.code === 'NEW_ATTENDANCES';

                const prev = participants.getOrElse([]);
                const matched = prev.find(p => {
                    const memberNo = p.membershipNumber.toUpperCase();
                    if (cleanedText.startsWith("M")) {
                        return memberNo === cleanedText || memberNo === cleanedText.substring(1);
                    } else {
                        return memberNo === cleanedText;
                    }
                });

                if (matched) {
                    setParticipants(Monad.success(prev.map(p => p.membershipNumber === matched.membershipNumber ? ({ ...p, checkedIn: "yes" }) : p)));
                }

                setScannerCheckInState({
                    status: isSuccess ? "success" : "error",
                    maResult: result
                });

                return result.fdTimeOut || 4000;
            }).catch(error => {
                console.error("Error in MA scanner check-in", error.response?.status, error.response?.data ?? error);
                const responseResult = getMaCheckinResult(error.response?.data);
                const result = responseResult ?? {
                    state: error.response?.status ?? 500,
                    code: error.code ?? "ERROR",
                    soundSignal: "error",
                    fdHeading: "Fel",
                    fdMessage: error.response?.data?.message ?? "Ett nätverksfel inträffade",
                    fdBgColor: "#cc1922",
                    fdTimeOut: 4000,
                    fdExtraInfo: error.response?.data ? JSON.stringify(error.response.data) : undefined
                };
                setScannerCheckInState({
                    status: "error",
                    maResult: result
                });
                return 4000;
            });
        }

        const participant = participants.getOrElse([]).find(p => {
            const memberNo = p.membershipNumber.toUpperCase();
            if (cleanedText.startsWith("M")) {
                return memberNo === cleanedText || memberNo === cleanedText.substring(1);
            } else if (cleanedText.startsWith("U")) {
                return memberNo === cleanedText;
            } else {
                return memberNo === cleanedText;
            }
        });

        if (!participant) {
            setScannerCheckInState({ status: "notBooked" });
            return Promise.resolve();
        }

        setScannerCheckInState({ status: "loading" });

        return (checkOut
            ? checkOutParticipant(selectedEvent!.eventId, participant.membershipNumber)
            : checkInParticipant(selectedEvent!.eventId, participant.membershipNumber)).then(response => {
                var prev = participants.getOrElse([]);
                switch (response.data.checkInStatus) {
                    case 'success':
                        setParticipants(Monad.success(prev.map(p => p.membershipNumber === participant.membershipNumber ? ({ ...p, checkedIn: "yes" }) : p)));

                        if (shouldAlsoCheckInPartner(response.data, prev)) {
                            setScannerCheckInState({
                                status: 'success-ask-partner',
                                booking: response.data.booking,
                                partnerParticipant: prev.find(x => membershipNumbersMatch(x.membershipNumber, response.data.booking?.partnerMembershipNumber))
                            });
                        } else {
                            setScannerCheckInState({
                                status: "success",
                                booking: response.data.booking
                            });
                        }
                        break;
                    case 'alreadyCheckedIn':
                        setParticipants(Monad.success(prev.map(p => p.membershipNumber === participant.membershipNumber ? ({ ...p, checkedIn: "yes" }) : p)));
                        setScannerCheckInState({
                            status: "alreadyCheckedIn",
                            booking: response.data.booking
                        });
                        break;
                    case 'notBooked':
                        setScannerCheckInState({ status: "notBooked" });
                        break;
                    case 'removed':
                        setParticipants(Monad.success(prev.map(p => p.membershipNumber === participant.membershipNumber ? ({ ...p, checkedIn: "no" }) : p)));
                        setScannerCheckInState({
                            status: "removed",
                            booking: response.data.booking
                        });
                        break;
                    default:
                        setScannerCheckInState({ status: "idle" });
                }
            }).catch(error => {
                console.error("Error checking in participant with scanner", error);
                setScannerCheckInState({ status: "error" });
            });
    }

    const handleScannerCheckInPartner = async (partnerParticipant: ApiParticipant) => {

        setScannerCheckInState(prev => ({ ...prev, status: "partner-checkIn-loading" }))

        checkInParticipant(selectedEvent!.eventId, partnerParticipant.membershipNumber).then(response => {
            console.log(response.data.checkInStatus);
            var prev = participants.getOrElse([]);

            switch (response.data.checkInStatus) {
                case 'success':
                    setParticipants(Monad.success(prev.map(p => p.membershipNumber === partnerParticipant.membershipNumber ? ({ ...p, checkedIn: "yes" }) : p)));

                    setScannerCheckInState(prev => ({
                        ...prev,
                        status: 'partner-success'
                    }));
                    break;
                case 'alreadyCheckedIn':
                    setParticipants(Monad.success(prev.map(p => p.membershipNumber === partnerParticipant.membershipNumber ? ({ ...p, checkedIn: "yes" }) : p)));
                    setScannerCheckInState(prev => ({
                        ...prev,
                        status: 'partner-alreadyCheckedIn',
                    }));
                    break;
                default:
                    setScannerCheckInState(prev => ({ ...prev, status: 'idle' }));
            }
        }).catch(error => {
            console.error("Error checking in partner", error);
            setScannerCheckInState(prev => ({
                ...prev,
                status: 'partner-error',
                partnerParticipant: partnerParticipant
            }));
        });
    }

    useEffect(() => {
        if (!eventsContext.eventModalOpen) {
            setModalNavigationState(EventModalNavigationState.eventInfo);
        }

        if (!selectedEvent) {
            return
        };
        authContext.user.match({
            loggedIn: async (user) => {
                if (shouldRenderParticipants(selectedEvent, user)) {
                    setParticipants(Monad.loading());
                    fetchParticipants(selectedEvent.eventId)
                        .then((participantsResponse) => {
                            setParticipants(Monad.success(participantsResponse.data));
                        })
                        .catch((error) => {
                            console.error(error);
                            setParticipants(Monad.error("Kunde inte hämta deltagare."));
                        });
                }
            }
        });
        return () => {
            setCheckInState({});
            setParticipantSearchTerm("");
            setParticipantCheckInStatusFilter("all");
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventsContext.eventModalOpen]);


    return (
        <BootstrapDialog
            onClose={eventsContext!.closeEventModal}
            aria-labelledby="customized-dialog-title"
            open={eventsContext!.eventModalOpen}
        >
            <DialogTitle sx={{ m: 0, p: 2, pr: 11 }} id="customized-dialog-title">
                {modalNavigationState === EventModalNavigationState.eventInfo && selectedEvent?.heading}
                {modalNavigationState === EventModalNavigationState.participantList && "Incheckning"}
                {modalNavigationState === EventModalNavigationState.scanner && "Incheckning QR-kod"}
            </DialogTitle>
            {modalNavigationState === EventModalNavigationState.scanner && selectedEvent?.source === 'minaaktiviteter' &&
                <IconButton
                    aria-label="search-users"
                    onClick={() => setScannerSearchOpenRequest((current) => current + 1)}
                    size="small"
                    sx={(theme) => ({
                        position: 'absolute',
                        right: 50,
                        top: 12,
                        color: theme.palette.grey[500],
                    })}
                >
                    <SearchIcon fontSize="small" />
                </IconButton>
            }
            <IconButton
                aria-label="close"
                onClick={eventsContext!.closeEventModal}
                sx={(theme) => ({
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[500],
                })}
            >
                <CloseIcon />
            </IconButton>
            {modalNavigationState === EventModalNavigationState.participantList && authContext.user.match({
                loggedIn(user) {
                    const displayCheckinStatusSelect = selectedEvent && canCheckIn(selectedEvent, user);
                    return (<Box sx={{ padding: "0 12px 12px 12px" }}>
                        <Grid container spacing={1}>
                            <Grid size={displayCheckinStatusSelect ? 8 : 12}>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel htmlFor="outlined-adornment-search-participant">{t("participants.search")}</InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-search-participant"
                                        type="text"
                                        value={participantSearchTerm}
                                        onChange={evt => setParticipantSearchTerm(evt.target.value)}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton edge="end" onClick={() => setParticipantSearchTerm("")}>
                                                    <HighlightOffIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                        label={t("participants.search")}
                                    />
                                </FormControl>
                            </Grid>
                            {displayCheckinStatusSelect &&
                                <Grid size={4}>
                                    <FormControl fullWidth>
                                        <InputLabel id="checkIn-status-select-label">{t("scanner.status_title")}</InputLabel>
                                        <Select
                                            labelId="checkIn-status-select-label"
                                            id="checkIn-status-select"
                                            label={t("scanner.status_title")}
                                            value={participantCheckInStatusFilter}
                                            onChange={(evt) => setParticipantCheckInStatusFilter(evt.target.value as "all" | "checkedIn" | "checkedOut")}
                                        >
                                            <MenuItem value={"all"}>{t("participants.filter_all")}</MenuItem>
                                            <MenuItem value={"checkedIn"}>{t("participants.filter_checked_in")}</MenuItem>
                                            <MenuItem value={"checkedOut"}>{t("participants.filter_checked_out")}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            }
                        </Grid>

                        {displayCheckinStatusSelect && participants.match({
                            success: (data) => <Typography component='p' mt={1}>Antal incheckade: {data.filter(p => p.checkedIn === "yes").length} av {data.length}</Typography>

                        })}

                    </Box>)
                }
            })

            }
            <DialogContent dividers>
                {modalNavigationState === EventModalNavigationState.eventInfo &&
                    <EventInformationModalContent changeModalState={(state: EventModalNavigationState) => setModalNavigationState(state)} />
                }
                {modalNavigationState === EventModalNavigationState.participantList &&
                    <>
                        {authContext.user.match({
                            loggedIn(user) {
                                return participants.match({
                                    success(data) {
                                        const filter = () => {
                                            var filtered = data.filter(p => participantSearchTerm.length ? p.name.toLocaleLowerCase().indexOf(participantSearchTerm.toLocaleLowerCase()) > -1 : p)

                                            switch (participantCheckInStatusFilter) {
                                                case 'all':
                                                    return filtered;
                                                case 'checkedIn':
                                                    return filtered.filter(p => p.checkedIn === "yes");
                                                case 'checkedOut':
                                                    return filtered.filter(p => p.checkedIn !== "yes");
                                            }
                                        }

                                        const filteredParticipants = filter();
                                        return <>
                                            {selectedEvent && !canCheckIn(selectedEvent, user) &&
                                                <ParticipantsList participants={filteredParticipants} />
                                            }
                                            {selectedEvent && canCheckIn(selectedEvent, user) &&
                                                <ParticipantsAccordion
                                                    participants={filteredParticipants}
                                                    onCheckIn={handleCheckIn}
                                                    onCheckInPartner={handleCheckInPartner}
                                                    checkInState={checkInState}
                                                    isMaEvent={selectedEvent.source === 'minaaktiviteter'} />
                                            }
                                        </>
                                    },
                                    loading: () => (
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <CircularProgress size="6rem" />
                                            <Typography variant="h6" style={{ marginTop: '16px' }}>
                                                Hämtar deltagare ...
                                            </Typography>
                                        </Box>
                                    ),
                                    idle: () => (
                                        <Alert severity='info'>Inga deltagare att visa</Alert>
                                    ),
                                    error: () => (
                                        <Alert severity='error'>Kunde inte hämta deltagare</Alert>
                                    )
                                })
                            }
                        })}
                    </>
                }
                {modalNavigationState === EventModalNavigationState.scanner &&
                    <QrCodeScanner
                        handleScannerCheckIn={handleScannerCheckIn}
                        handleLookupMaUser={fetchMaUserByMembershipNumber}
                        handleSearchMaUsers={searchMaUsers}
                        handleScannerCheckInPartner={handleScannerCheckInPartner}
                        scannerCheckInState={scannerCheckInState}
                        searchOpenRequest={scannerSearchOpenRequest}
                        isMaEvent={selectedEvent?.source === 'minaaktiviteter'}
                    />
                }


            </DialogContent>
            {selectedEvent && authContext.user.match({
                loggedIn: (user) =>
                    (shouldRenderParticipants(selectedEvent, user) || canCheckIn(selectedEvent, user)) &&
                    <DialogActions style={{ justifyContent: 'center' }}>
                        <Tabs value={modalNavigationState} onChange={(evt, newValue: EventModalNavigationState) => { setModalNavigationState(newValue); }} centered>
                            <Tab sx={{ padding: "9px 12px", fontSize: ".6rem" }} icon={<InfoOutlineIcon />} label={t("event_modal.information")} value={EventModalNavigationState.eventInfo} />
                            {shouldRenderParticipants(selectedEvent, user) &&
                                <Tab sx={{ padding: "9px 12px", fontSize: ".6rem" }} icon={<GroupsOutlinedIcon />} label={participants.match({
                                    loading: () => (<span style={{ display: 'flex', alignItems: 'center' }}>{t("event_modal.participants")} <CircularProgress sx={{ marginLeft: "5px" }} size={"1rem"} /></span>),
                                    success: (data) => (<>{`${t("event_modal.participants")} (${data.length})`}</>)
                                })} value={EventModalNavigationState.participantList} />
                            }
                            {canCheckIn(selectedEvent, user) &&
                                <Tab sx={{ padding: "9px 12px", fontSize: ".6rem" }} icon={<QrCodeScannerIcon />} label={t("event_modal.scanner")} value={EventModalNavigationState.scanner} />
                            }
                        </Tabs>
                    </DialogActions>
            })}
        </BootstrapDialog>
    );
};
